import { NgZone, OnDestroy, inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../environments/environment';
import { BrowserEventBusService, PageVisibilityHandler } from './event-bus.service';

@Injectable()
export abstract class BaseWebsocketService<T> implements OnDestroy, PageVisibilityHandler {
  protected zone = inject(NgZone);
  protected eventBus = inject(BrowserEventBusService);

  protected ws?: WebSocket;
  protected messagesSubject: BehaviorSubject<T[]>;
  protected currentNumber?: string;
  protected reconnectAttempts = 0;
  protected maxReconnectAttempts = 3;

  // The websocket endpoint path (e.g., 'status' or 'messages')
  protected abstract readonly wsEndpoint: string;
  // Service name for logging
  protected abstract readonly serviceName: string;

  constructor(initialValue: T[] = []) {
    this.messagesSubject = new BehaviorSubject<T[]>(initialValue);
    this.eventBus.registerVisibilityHandler(this);
  }

  ngOnDestroy(): void {
    this.eventBus.unregisterVisibilityHandler(this);
    this.disconnect();
  }

  onPageVisible(): void {
    if (this.currentNumber && !this.isConnected()) {
      console.log(`Page visible - reconnecting ${this.serviceName}`);
      this.connect(this.currentNumber);
      this.ws?.send(JSON.stringify({ type: "availability", available: true }));
    }
    else if (this.currentNumber && this.isConnected()) {
      console.log(`Page visible - setting ${this.serviceName} availability to true`);
      this.ws?.send(JSON.stringify({ type: "availability", available: true }));
    }
  }

  onPageHidden(): void {
    if (this.isConnected()) {
      console.log(`Page hidden - setting ${this.serviceName} availability to false`);
      this.ws?.send(JSON.stringify({ type: "availability", available: false }));
    }
  }

  protected isConnected(): boolean {
    return this.ws !== undefined &&
           (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING);
  }

  protected isClosed(): boolean {
    return this.ws === undefined ||
           this.ws.readyState === WebSocket.CLOSED ||
           this.ws.readyState === WebSocket.CLOSING;
  }

  protected closeWebSocket(): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.close(1000, 'Client disconnect');
    }
    this.ws = undefined;
  }

  connect(number: string): void {
    // If already connected, skip
    if (this.isConnected()) {
      console.log(`${this.serviceName} already connected, skipping`);
      return;
    }

    // Close any existing connection that might be in a bad state
    this.closeWebSocket();

    console.log(`${this.serviceName}.connect() called with number:`, number);
    this.currentNumber = number;
    this.reconnectAttempts = 0;

    const token = localStorage.getItem('access-token');
    const encodedNumber = encodeURIComponent(number);
    const wsUrl = `wss://${environment.messagingApi}/ws/${this.wsEndpoint}?number=${encodedNumber}&token=${token}`;

    console.log(`${this.serviceName} connecting - token present:`, !!token);

    if (!token) {
      console.error(`${this.serviceName} connect failed - no access token`);
      return;
    }

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        console.log(`${this.serviceName} connected`);
        this.reconnectAttempts = 0;
      };

      this.ws.onmessage = (event) => this.handleMessage(event);

      this.ws.onclose = (event) => {
        console.log(`${this.serviceName} closed`, event.code, event.reason);

        if (!event.wasClean && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Attempting to reconnect ${this.serviceName}... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          setTimeout(() => {
            if (this.currentNumber) {
              this.connect(this.currentNumber);
            }
          }, 1000 * this.reconnectAttempts);
        }
      };

      this.ws.onerror = (error) => {
        console.error(`${this.serviceName} error:`, error);

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          console.log(`Retrying ${this.serviceName} connection due to error... (${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          const retryDelay = 2000 * this.reconnectAttempts;
          setTimeout(() => {
            if (this.currentNumber) {
              this.connect(this.currentNumber);
            }
          }, retryDelay);
        }
      };
    } catch (error) {
      console.error(`Failed to create ${this.serviceName}:`, error);
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this.reconnectAttempts++;
        setTimeout(() => this.connect(number), 3000);
      }
    }
  }

  disconnect(): void {
    this.closeWebSocket();
    this.currentNumber = undefined;
    this.reconnectAttempts = 0;
  }

  // Handle incoming websocket message - subclasses implement their specific logic
  protected abstract handleMessage(event: MessageEvent): void;
}
